import React from 'react';
import { motion } from 'framer-motion';
import { getScoreColor } from './InteractiveGamePage';
import {
  Box, 
  Typography, 
  Button, 
  Divider, 
  Paper, 
  Grid,
  CircularProgress,
  CheckCircleIcon,
  Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './styles/ScoreSummary.css';

/**
 * Componente per il riepilogo dei punteggi di tutti i giochi
 */
const ScoreSummary = ({
  gameData,
  completedGames,
  onShowGameDetails,
  onBackToGames,
  onResetGame,
  onSaveResults,
  resultsSaving,
  resultsSaved,
  isVerification
}) => {
  // Calcolo il punteggio totale (media dei punteggi)
  // Escludiamo le attività di tipo 'scrittura', 'lettura' e 'video' dal calcolo della media
  const validScores = completedGames
    .filter(game => {
      // Escludiamo i tipi di gioco che non devono influire sul punteggio
      const skipTypes = ['scrittura', 'lettura', 'video'];
      if (skipTypes.includes(game.type)) {
        console.log(`ScoreSummary: Escludo dal calcolo del punteggio l'attività di tipo ${game.type}`, game.chatId);
        return false;
      }
      // Includiamo solo i giochi con punteggi numerici validi
      return typeof game.score === 'number' && !isNaN(game.score);
    })
    .map(game => game.score);

  console.log('ScoreSummary: Punteggi validi considerati per la media:', validScores);
  
  const totalScore = validScores.length > 0 
    ? Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) 
    : 0;
    
  console.log('ScoreSummary: Punteggio totale calcolato:', totalScore);

  // Ordinamento dei giochi completati per posizione (se disponibile) o per data di completamento
  const orderedGames = [...completedGames].sort((a, b) => {
    // Se disponiamo delle informazioni sulla posizione dal gameData
    const gameDataChats = gameData?.game_data?.chats || [];
    const chatA = gameDataChats.find(chat => chat.id === a.chatId);
    const chatB = gameDataChats.find(chat => chat.id === b.chatId);
    
    // Usiamo la posizione se disponibile, altrimenti ordiniamo per data di completamento
    if (chatA?.position !== undefined && chatB?.position !== undefined) {
      return chatA.position - chatB.position;
    }
    
    // Fallback all'ordinamento per data di completamento
    return new Date(a.completedAt || 0) - new Date(b.completedAt || 0);
  });

  // Funzione per ottenere il titolo della chat
  const getChatTitle = (chatId) => {
    const chat = gameData?.game_data?.chats.find(c => c.id === chatId);
    return chat?.title || `Attività ${chatId}`;
  };

  // Funzione per ottenere l'emoji basata sul punteggio
  const getScoreEmoji = (score) => {
    if (score >= 90) return '🏆';
    if (score >= 80) return '😄';
    if (score >= 70) return '🙂';
    if (score >= 60) return '😐';
    return '😕';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="score-summary-container"
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold',
          color: '#333'
        }}>
          Riepilogo Finale
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Pulsante per salvare i risultati - visibile solo per esercitazioni o se non è stato ancora salvato */}
          {(!isVerification || !resultsSaved) && (
            <Tooltip title={resultsSaved ? "Risultati già salvati" : "Salva i risultati"}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={resultsSaved ? <CheckCircleOutlineIcon /> : (resultsSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />)}
                  onClick={onSaveResults}
                  disabled={resultsSaved || resultsSaving}
                  sx={{
                    backgroundColor: resultsSaved ? '#4caf50' : '#1976d2',
                    '&:hover': {
                      backgroundColor: resultsSaved ? '#43a047' : '#1565c0',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: resultsSaved ? '#81c784' : undefined,
                      color: resultsSaved ? '#fff' : undefined,
                    }
                  }}
                >
                  {resultsSaved 
                    ? "Risultati Salvati" 
                    : (resultsSaving ? "Salvando..." : "Salva Risultati")}
                </Button>
              </span>
            </Tooltip>
          )}
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={onResetGame}
          >
            Ricomincia
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToGames}
          >
            Torna ai Giochi
          </Button>
        </Box>
      </Box>

      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '1rem',
          background: 'linear-gradient(to right, #f9f9f9, #ffffff)',
          border: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              Punteggio totale
            </Typography>
            <Typography variant="h3" sx={{ 
              fontWeight: 'bold', 
              color: getScoreColor(totalScore) 
            }}>
              {totalScore}/100 {getScoreEmoji(totalScore)}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Attività completate: <strong>{completedGames.length}</strong> su <strong>{gameData?.game_data?.chats?.length || 0}</strong>
            </Typography>
            
            {/* Stato del salvataggio */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: resultsSaved ? 'success.main' : 'text.secondary',
              mt: 1
            }}>
              {resultsSaved && <CheckCircleOutlineIcon sx={{ mr: 0.5, fontSize: '1rem' }} />}
              <Typography variant="body2">
                {resultsSaved 
                  ? "Risultati salvati sul server" 
                  : isVerification
                    ? "Salvataggio automatico in corso..." 
                    : "I risultati non sono ancora stati salvati"}
              </Typography>
              {!resultsSaved && isVerification && (
                <CircularProgress size={14} sx={{ ml: 1 }} color="primary" />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2, color: '#555' }}>
        Dettaglio per attività
      </Typography>

      <Grid container spacing={2}>
        {orderedGames.map((game, index) => {
          // Trova la chat corrispondente nei dati del gioco
          const chat = gameData?.game_data?.chats?.find(c => c.id === game.chatId);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={game.uniqueId || index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2,
                    borderRadius: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => onShowGameDetails(game, chat)}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    color: '#333'
                  }}>
                    {getChatTitle(game.chatId)}
                  </Typography>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 'auto'
                  }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {game.type === 'quiz' && 'Quiz'}
                      {game.type === 'matchpairs' && 'Abbina le coppie'}
                      {game.type === 'fillthebox' && 'Completa il testo'}
                      {game.type === 'rearrange' && 'Riordina'}
                      {game.type === 'crossword' && 'Cruciverba'}
                      {game.type === 'selectgroup' && 'Classifica'}
                      {game.type === 'scrittura' && 'Testo/Video'}
                    </Typography>
                    
                    {typeof game.score === 'number' ? (
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold',
                        color: getScoreColor(game.score)
                      }}>
                        {game.score}/100
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        Completato
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </motion.div>
  );
};

export default ScoreSummary; 